import React, { createContext, useContext, useState, useCallback, useRef } from "react";
import { FaCheckCircle, FaExclamationCircle, FaExclamationTriangle, FaInfoCircle, FaTimes } from "react-icons/fa";

const ToastContext = createContext(null);

export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used within ToastProvider");
  return ctx;
};

const TOAST_COLORS = {
  success: { bg: "#f0fdf4", border: "#bbf7d0", color: "#166534", icon: FaCheckCircle, iconColor: "#22c55e" },
  error: { bg: "#fef2f2", border: "#fecaca", color: "#991b1b", icon: FaExclamationCircle, iconColor: "#ef4444" },
  warning: { bg: "#fffbeb", border: "#fde68a", color: "#92400e", icon: FaExclamationTriangle, iconColor: "#f59e0b" },
  info: { bg: "#eff6ff", border: "#bfdbfe", color: "#1e40af", icon: FaInfoCircle, iconColor: "#3b82f6" },
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const idRef = useRef(0);

  const showToast = useCallback((message, type = "info") => {
    const id = ++idRef.current;
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 4000);
  }, []);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      {/* Toast container */}
      <div style={styles.container}>
        {toasts.map((toast) => {
          const config = TOAST_COLORS[toast.type] || TOAST_COLORS.info;
          const Icon = config.icon;
          return (
            <div
              key={toast.id}
              style={{
                ...styles.toast,
                backgroundColor: config.bg,
                borderColor: config.border,
                color: config.color,
              }}
            >
              <Icon size={16} color={config.iconColor} style={{ flexShrink: 0 }} />
              <span style={styles.message}>{toast.message}</span>
              <button
                style={{ ...styles.closeBtn, color: config.color }}
                onClick={() => removeToast(toast.id)}
              >
                <FaTimes size={12} />
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
};

const styles = {
  container: {
    position: "fixed",
    top: 16,
    left: "50%",
    transform: "translateX(-50%)",
    zIndex: 10000,
    display: "flex",
    flexDirection: "column",
    gap: 8,
    pointerEvents: "none",
  },
  toast: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "12px 16px",
    borderRadius: 10,
    border: "1px solid",
    boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
    fontFamily: "'FT Habit', -apple-system, BlinkMacSystemFont, sans-serif",
    fontSize: 13,
    fontWeight: 400,
    minWidth: 280,
    maxWidth: 480,
    pointerEvents: "auto",
    animation: "slideDown 0.3s ease",
  },
  message: {
    flex: 1,
  },
  closeBtn: {
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: 2,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    opacity: 0.6,
    flexShrink: 0,
  },
};
